// Tumble/Cascade 引擎 — 對應 Unity SlotStateMachine 的 DestroySymbol→FillSymbol→(TransformSymbol) 迴圈：
// 評估中獎 → 消除命中格 → 其餘下沉、頂部補新符號 → 再評估，直到無新中獎為止。
// Tracker 頂輪的格子若參與中獎，同樣會消除、倖存格遞補、缺口補新符號（見 dropAndFillTracker）；
// 疊加慣例本身（1 個 overlay 位置對應 1 個主輪）不變，見 waysEvaluator.ts 說明。

import { MAIN_REEL_COUNT, MAX_TUMBLE_STEPS, TRACKER_REEL_INDEX, WILD_FILL_CHANCE, WILD_SYMBOL_ID } from '../constants'
import { pickReelSymbol } from '../config/reelStrips'
import { mockPayoutMultiplier, type ReelsMatched } from '../config/payTable'
import { symbolById } from '../config/symbols'
import { logger } from '../../framework/core/Logger'
import { evaluateWays, type WayWin } from './waysEvaluator'
import type { Board, BoardCell, CellRef, ReelColumn } from '../model/board'

/** 描述盤面某一格「消除連鎖後」的來源：倖存符號從 fromRow 落下，或 fromRow 為 null 代表全新補位符號（從畫面上方落入）。 */
export interface FallEntry {
  readonly finalRow: number
  readonly fromRow: number | null
}

/**
 * 一格「符號變 Wild」的變身（對應 Unity TransformSymbol 狀態的資料通道）：
 * 補牌先以 fromSymbol（OriginalSymbol）落下，變身演出後才換成 Wild（TumbleSymbol）。
 * 對照 Unity GameLogic.DiffTransformSymbolPositions（比對 OriginalSymbol vs TumbleSymbol）
 * ＋ ReelController.PlayChangeAnimation（原符號骨骼 win→change→SetWild）。
 */
export interface WildTransform {
  readonly ref: CellRef
  /** 變身前顯示的原符號 id（補牌落下時顯示這個，change 動畫結束才換 WW）。 */
  readonly fromSymbol: number
}

export interface TumbleStep {
  readonly boardBefore: Board
  readonly boardAfter: Board
  readonly trackerBefore: ReelColumn
  readonly trackerAfter: ReelColumn
  readonly wins: readonly WayWin[]
  readonly win: number
  /**
   * 本步驟套用的倍率（主遊戲恆為 1；Free Game 中為「進入此步時」的累積倍率）。
   * 對應後端 TumbleResult.Multiplier。供畫面在 FG 決定是否播主倍率飛入強調（見 GameController）。
   */
  readonly multiplier: number
  /** 本步驟補牌後變身為 Wild 的格子（boardAfter 上該格已是 Wild；演出時先顯示 fromSymbol 再變身）。驅動 ReelsView 變身演出。 */
  readonly wildTransforms: readonly WildTransform[]
  /**
   * 本步驟「由變身而來的 Wild」格座標（對照 Unity GameLogic.DiffTransformSymbolPositions，逐 TumbleResult 判定）。
   * 純資料標記，不驅動演出（演出用 wildTransforms）；Mock 路徑＝本步補牌變身格，後端路徑＝該 TumbleResult 自身 Original↔Tumble 差異。
   */
  readonly newWildCells: readonly CellRef[]
  /** 每個主輪各格的落下路徑（index 對應 reel），供畫面播放掉落動畫；只涵蓋主輪，不含 Tracker 頂輪。 */
  readonly fallMap: readonly (readonly FallEntry[])[]
  /**
   * Tracker 頂輪的落下路徑（finalRow/fromRow 都是 trackerColumn 的疊加位置 index，非主輪 reel index）：
   * fromRow 非 null＝倖存符號由該位置遞補過來（畫面上往左移動），null＝新補入的符號（畫面上自右側滑入）。
   */
  readonly trackerFall: readonly FallEntry[]
}

export interface TumbleOutcome {
  readonly steps: readonly TumbleStep[]
  readonly totalWin: number
  readonly finalBoard: Board
  /** 本次 Spin 結束時 Tracker 頂輪的最終內容（若曾中獎消除則已更新；沒有任何連鎖時等同起始內容）。 */
  readonly trackerColumn: ReelColumn
}

/**
 * [MOCK] 可變身為 Wild 的原符號（僅 H2/H3/H4 的骨骼有 change_1x{n} 變身動畫，
 * 對應三個有專屬 Wild 造型 W2/W3/W4 的角色符號；實際變身格由後端 OriginalSymbol/TumbleSymbol 指定）。
 */
const TRANSFORMABLE_SYMBOLS: readonly number[] = [2, 3, 4]

function randomTransformSource(): number {
  return TRANSFORMABLE_SYMBOLS[Math.floor(Math.random() * TRANSFORMABLE_SYMBOLS.length)]!
}

function randomTrackerCell(): BoardCell {
  return { symbol: Math.random() < WILD_FILL_CHANCE ? WILD_SYMBOL_ID : pickReelSymbol(TRACKER_REEL_INDEX) }
}

/**
 * 依命中格推一條輪的落下路徑：倖存格保持原本相對順序、整體沉到「底部」（index 大的一端），
 * 缺口留在入口端（index 小的一端）由新符號補上。主輪的 index 是 row（底＝畫面下方、入口在上方）；
 * Tracker 頂輪的 index 是疊加位置（反序疊加，底＝畫面最左、入口在最右，見 constants.ts）。
 */
export function fallEntriesFor(length: number, winningIndices: ReadonlySet<number>): FallEntry[] {
  const survivors: number[] = []
  for (let i = 0; i < length; i++) {
    if (!winningIndices.has(i)) survivors.push(i)
  }
  const missing = length - survivors.length
  return [
    ...Array.from({ length: missing }, (_, i): FallEntry => ({ finalRow: i, fromRow: null })),
    ...survivors.map((fromRow, i): FallEntry => ({ finalRow: missing + i, fromRow })),
  ]
}

/**
 * 單一欄位的消除補位：倖存格保持原本相對順序、整體下沉到底部，缺口由新符號從頂部補上。
 * 同時回傳「每個最終列位置的落下路徑」，供畫面得知該用哪個舊位置的貼圖、從哪一列的螢幕座標開始播放掉落動畫。
 * [MOCK] 補位符號以 WILD_FILL_CHANCE 機率走「變身 Wild」通道：盤面資料直接為 Wild、
 * 另記錄 fromSymbol（H2/H3/H4 擇一）供演出先落原符號再變身（對照 Unity forceOriginalId 補牌）。
 */
function dropAndFillColumn(
  column: ReelColumn,
  reelIndex: number,
  winningRows: ReadonlySet<number>,
): { column: ReelColumn; fall: readonly FallEntry[]; transforms: readonly { row: number; fromSymbol: number }[] } {
  const fall = fallEntriesFor(column.length, winningRows)
  const transforms: { row: number; fromSymbol: number }[] = []
  const newColumn = fall.map((entry) => {
    if (entry.fromRow !== null) return { ...column[entry.fromRow]! }
    if (Math.random() < WILD_FILL_CHANCE) {
      const fromSymbol = randomTransformSource()
      transforms.push({ row: entry.finalRow, fromSymbol })
      // viewSymbolId 記錄原符號：變身完成後顯示角色專屬百搭造型（W2/W3/W4），並隨此格下墜持久保留
      // （對照 Unity SetWild 只改 SymbolId、ViewSymbolId 維持原始 id）。
      return { symbol: WILD_SYMBOL_ID, viewSymbolId: fromSymbol }
    }
    return { symbol: pickReelSymbol(reelIndex) }
  })

  return { column: newColumn, fall, transforms }
}

/** 消除命中格、其餘格子下沉、頂部補新符號，並回傳每欄的落下路徑供畫面播放掉落動畫。補位符號從該輪的真實輪帶抽樣。 */
function dropAndFill(
  board: Board,
  winningCellsByReel: ReadonlyMap<number, ReadonlySet<number>>,
): { board: Board; fallMap: readonly (readonly FallEntry[])[]; wildTransforms: readonly WildTransform[] } {
  const fallMap: (readonly FallEntry[])[] = []
  const wildTransforms: WildTransform[] = []
  const newBoard = board.map((column, reelIndex) => {
    const winningRows = winningCellsByReel.get(reelIndex) ?? new Set<number>()
    const result = dropAndFillColumn(column, reelIndex, winningRows)
    fallMap.push(result.fall)
    for (const t of result.transforms) wildTransforms.push({ ref: { reel: reelIndex, row: t.row }, fromSymbol: t.fromSymbol })
    return result.column
  })
  return { board: newBoard, fallMap, wildTransforms }
}

/**
 * Tracker 頂輪的消除補位：與主輪同一套下沉語意，只是這條輪橫著擺（反序疊加，見 constants.ts）——
 * 中獎格清除後，倖存符號往畫面左側（index 遞增＝這條輪的「底部」）遞補，缺口留在入口端
 * （index 0＝畫面最右）由新符號補入，不再是「原地換一張新符號、把消掉的那格留在原位」。
 */
function dropAndFillTracker(
  trackerColumn: ReelColumn,
  winningPositions: ReadonlySet<number>,
): { trackerColumn: ReelColumn; fall: readonly FallEntry[] } {
  const fall = fallEntriesFor(trackerColumn.length, winningPositions)
  const newColumn = fall.map((entry) => (entry.fromRow === null ? randomTrackerCell() : { ...trackerColumn[entry.fromRow]! }))
  return { trackerColumn: newColumn, fall }
}

function winAmount(win: WayWin, betPerWay: number): number {
  const tier = symbolById(win.symbolId).tier
  const reelsMatched = Math.min(win.reelsMatched, MAIN_REEL_COUNT) as ReelsMatched
  return win.ways * mockPayoutMultiplier(tier, reelsMatched) * betPerWay
}

/**
 * 解出一次 Spin 的完整消除連鎖序列：評估中獎 → 消除補位 → 再評估，直到無新中獎或達安全上限為止。
 */
export function resolveTumbles(initialBoard: Board, initialTrackerColumn: ReelColumn, betPerWay: number): TumbleOutcome {
  const steps: TumbleStep[] = []
  let board = initialBoard
  let trackerColumn = initialTrackerColumn
  let totalWin = 0

  for (let stepIndex = 0; stepIndex < MAX_TUMBLE_STEPS; stepIndex++) {
    const wins = evaluateWays(board, trackerColumn)
    if (wins.length === 0) break

    const winningCellsByReel = new Map<number, Set<number>>()
    const winningTrackerPositions = new Set<number>()
    for (const cell of wins.flatMap((win) => win.cells)) {
      if (cell.reel === TRACKER_REEL_INDEX) {
        winningTrackerPositions.add(cell.row)
        continue
      }
      const rows = winningCellsByReel.get(cell.reel) ?? new Set<number>()
      rows.add(cell.row)
      winningCellsByReel.set(cell.reel, rows)
    }

    const boardBefore = board
    const trackerBefore = trackerColumn
    const { board: boardAfter, fallMap, wildTransforms } = dropAndFill(board, winningCellsByReel)
    const { trackerColumn: trackerAfter, fall: trackerFall } = dropAndFillTracker(trackerColumn, winningTrackerPositions)
    const stepWin = wins.reduce((sum, win) => sum + winAmount(win, betPerWay), 0)

    steps.push({
      boardBefore,
      boardAfter,
      trackerBefore,
      trackerAfter,
      wins,
      win: stepWin,
      multiplier: 1, // 主遊戲步驟恆為 1；FG 倍率由 applyFreeGameMultiplier 覆寫。
      wildTransforms,
      newWildCells: wildTransforms.map((t) => t.ref),
      fallMap,
      trackerFall,
    })
    totalWin += stepWin
    board = boardAfter
    trackerColumn = trackerAfter

    if (stepIndex === MAX_TUMBLE_STEPS - 1) {
      logger.warn('resolveTumbles 達到安全上限 MAX_TUMBLE_STEPS，提前結束連鎖', { totalWin })
    }
  }

  return { steps, totalWin, finalBoard: board, trackerColumn }
}

/**
 * 套用 Free Game 的跨步驟遞增倍率（[ASSUMED] 每次「有贏分」的連鎖步驟後 +FG_MULTIPLIER_INCREMENT，
 * 倍率本身跨步驟保留，見 constants.ts 的假設說明）。回傳套用倍率後的步驟與倍率終值（供下一次 Spin 延續）。
 */
export function applyFreeGameMultiplier(
  outcome: TumbleOutcome,
  startingMultiplier: number,
  multiplierIncrement: number,
): { steps: readonly TumbleStep[]; totalWin: number; endingMultiplier: number } {
  let multiplier = startingMultiplier
  let totalWin = 0
  const steps: TumbleStep[] = []

  for (const step of outcome.steps) {
    const appliedWin = step.win * multiplier
    // multiplier 記錄「進入此步時」的累積倍率（供畫面判斷主倍率飛入）；win 為套用倍率後的實得贏分。
    steps.push({ ...step, win: appliedWin, multiplier })
    totalWin += appliedWin
    if (step.win > 0) multiplier += multiplierIncrement
  }

  return { steps, totalWin, endingMultiplier: multiplier }
}
